<template>
  <div>
  <index-card fluid>
    <template slot="header">
      <v-row>
        <v-col class="white--text d-flex justify-space-between headline">
          <div class="d-flex align-center">
            <v-btn icon color="blue-grey darken-1" :to="{ name: 'Home' }">
              <v-icon color="white">mdi-arrow-left</v-icon>
            </v-btn>
          </div>
          <div>Dados financeiros consolidados</div>
          <div class="d-flex align-center flex-wrap justify-end" style="gap: 8px;">
            <v-chip color="white">
              {{ referenceDateLabel }}
            </v-chip>
            <BtnCreateDataExport
              api_model="FinancialConsolidatedReport"
              action="FinancialConsolidatedReports"
              :filters="{ reference_date: referenceDate }"
              :file_name="'consolidado_financeiro_' + referenceDate"
              :light_toolbar="false"
            />
            <v-btn icon color="white" :loading="loading" aria-label="Atualizar dados" @click="getData()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>

    <template slot="data_table">
      <div>
        <v-row class="mt-1">
          <v-col cols="12" class="pb-0 mb-2">
            <v-card class="overview-card elevation-1">
              <v-card-text
                v-if="loading"
                class="py-3 overview-card-skeleton d-flex align-center justify-space-between flex-wrap"
                style="gap: 10px;"
              >
                <div class="flex-grow-1" style="min-width: 200px;">
                  <v-skeleton-loader type="text" width="200" class="mb-2 elevation-0 transparent-skeleton" />
                  <v-skeleton-loader type="heading" width="320" class="elevation-0 transparent-skeleton" />
                </div>
                <v-skeleton-loader type="chip" width="160" height="28" class="elevation-0 transparent-skeleton" />
              </v-card-text>
              <v-card-text v-else class="py-3">
                <div class="subtitle-1 font-weight-bold">Visão consolidada por modalidade</div>
                <div class="caption grey--text text--darken-1 mt-1">
                  Valores somados por região (galpão ou Grandes Cargas). Estoque só entra para galpões.
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <template v-if="loading">
          <v-row class="mt-2">
            <v-col
              v-for="totalsSkeletonIndex in 3"
              :key="'sk-totals-' + totalsSkeletonIndex"
              cols="12"
              md="4"
            >
              <v-skeleton-loader type="article" class="elevation-1" />
            </v-col>
          </v-row>
          <v-row class="mt-2">
            <v-col
              v-for="modalitySkeletonIndex in 2"
              :key="'sk-modality-' + modalitySkeletonIndex"
              cols="12"
            >
              <v-card class="modality-section-card elevation-1">
                <v-card-title class="py-2">
                  <v-skeleton-loader type="heading" width="260" class="elevation-0" />
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col
                      v-for="metricSkeletonIndex in 5"
                      :key="modalitySkeletonIndex + '-metric-' + metricSkeletonIndex"
                      cols="12"
                      sm="6"
                      md="4"
                      lg="2"
                    >
                      <v-skeleton-loader type="card" class="elevation-0" />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <template v-else-if="!consolidated.per_modality.length">
          <v-alert type="info" outlined class="mt-4" prominent>
            Nenhuma modalidade vinculada ao seu usuário para exibir o consolidado.
          </v-alert>
        </template>

        <template v-else>
          <v-card class="mt-2 totals-wrap-card elevation-1">
            <v-card-title class="subtitle-1 font-weight-bold">
              Totais entre todas as regiões
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4" class="py-2">
                  <v-card class="elevation-1 total-aggregate-card">
                    <v-card-title class="body-1 font-weight-bold pb-0">
                      Total galpões
                    </v-card-title>
                    <v-card-text>
                      <div
                        v-for="totalLine in totalLinesFor(consolidated.totals_retail, 'retail')"
                        :key="'tr-' + totalLine.key"
                        class="total-line"
                      >
                        <span class="total-line__label">{{ totalLine.label }}</span>
                        <span class="total-line__value">{{ totalLine.display }}</span>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4" class="py-2">
                  <v-card class="elevation-1 total-aggregate-card">
                    <v-card-title class="body-1 font-weight-bold pb-0">
                      Total Grandes Cargas
                    </v-card-title>
                    <v-card-text>
                      <div
                        v-for="totalLine in totalLinesFor(consolidated.totals_wholesale, 'wholesale')"
                        :key="'tw-' + totalLine.key"
                        class="total-line"
                      >
                        <span class="total-line__label">{{ totalLine.label }}</span>
                        <span class="total-line__value">{{ totalLine.display }}</span>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4" class="py-2">
                  <v-card class="elevation-1 total-aggregate-card total-aggregate-card--grand">
                    <v-card-title class="body-1 font-weight-bold pb-0">
                      Total geral
                    </v-card-title>
                    <v-card-text>
                      <div
                        v-for="totalLine in totalLinesFor(consolidated.grand_totals, 'grand')"
                        :key="'tg-' + totalLine.key"
                        class="total-line"
                      >
                        <span class="total-line__label">{{ totalLine.label }}</span>
                        <span class="total-line__value">{{ totalLine.display }}</span>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-row class="mt-4">
            <v-col
              v-for="modalityBlock in consolidated.per_modality"
              :key="modalityBlock.modality_id"
              cols="12"
            >
              <v-card class="modality-section-card elevation-1">
                <v-card-title class="subtitle-1 font-weight-bold d-flex align-center flex-wrap" style="gap: 8px;">
                  {{ modalityBlock.modality_name }}
                  <v-chip small color="primary" text-color="white">
                    {{ modalityBlock.solution === "wholesale" ? "Grandes Cargas" : "Galpão" }}
                  </v-chip>
                </v-card-title>
                <v-card-text class="pt-0">
                  <v-row>
                    <v-col
                      v-for="financialCard in financialCardDefs"
                      :key="modalityBlock.modality_id + '-' + financialCard.key"
                      cols="12"
                      sm="4"
                      md="4"
                      lg="4"
                      class="py-2"
                    >
                      <v-card :class="['elevation-1', 'summary-card', financialCard.cardClass]">
                        <v-card-text class="py-3">
                          <div class="d-flex justify-space-between align-start">
                            <div class="summary-card-title">{{ financialCard.title }}</div>
                            <v-icon small :color="financialCard.iconColor">{{ financialCard.icon }}</v-icon>
                          </div>
                          <div class="summary-card-value mt-2">
                            {{ toCurrency(modalityBlock.summary[financialCard.key]) }}
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" sm="6" md="6" lg="3" class="py-2">
                      <v-card class="elevation-1 summary-card summary-card--stock">
                        <v-card-text class="py-3">
                          <div class="d-flex justify-space-between align-start">
                            <div class="d-flex align-center summary-card-title">
                              Saldo em estoque
                              <v-tooltip v-if="modalityBlock.solution !== 'wholesale'" bottom max-width="280">
                                <template #activator="{ on, attrs }">
                                  <v-icon
                                    small
                                    class="ml-1 stock-value-header__icon"
                                    color="grey darken-1"
                                    v-bind="attrs"
                                    v-on="on"
                                  >
                                    mdi-information-outline
                                  </v-icon>
                                </template>
                                <span>Contagem do dia * Preço de compra (soma de todos os produtos)</span>
                              </v-tooltip>
                            </div>
                            <v-icon small color="#37474F">mdi-package-variant</v-icon>
                          </div>
                          <div v-if="modalityBlock.solution === 'wholesale'" class="summary-card-value summary-card-value--muted mt-2">
                            —
                          </div>
                          <div v-else class="summary-card-value mt-2">
                            {{ toCurrency(modalityBlock.summary.stock_balance_total) }}
                          </div>
                          <div
                            v-if="modalityBlock.solution === 'wholesale'"
                            class="caption grey--text text--darken-1 mt-1"
                          >
                            Grandes Cargas não possui estoque neste relatório.
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </div>
    </template>
  </index-card>
  </div>
</template>

<script>
import Api from "@/api/index"

const EMPTY_TOTALS = {
  accounts_receivable_open: 0,
  accounts_receivable_overdue: 0,
  accounts_receivable_protested: 0,
  checks_in_custody_total: 0,
  stock_balance_total: 0,
}

export default {
  name: "FinancialConsolidatedReports",
  components: {
    IndexCard: () => import("@/_backoffice/_registrations/components/shared/index/IndexCard.vue"),
    BtnCreateDataExport: () => import("@/components/data_export/BtnCreateDataExport.vue"),
  },
  data() {
    return {
      loading: false,
      consolidated: {
        per_modality: [],
        totals_retail: { ...EMPTY_TOTALS },
        totals_wholesale: { ...EMPTY_TOTALS },
        grand_totals: { ...EMPTY_TOTALS },
      },
      referenceDate: this.$moment().format("YYYY-MM-DD"),
      financialCardDefs: [
        {
          key: "accounts_receivable_open",
          title: "Contas a receber em aberto",
          icon: "mdi-cash-clock",
          iconColor: "#0D47A1",
          cardClass: "summary-card--open",
        },
        {
          key: "accounts_receivable_overdue",
          title: "Contas a receber vencidas (365 dias)",
          icon: "mdi-calendar-alert",
          iconColor: "#BF360C",
          cardClass: "summary-card--overdue",
        },
        {
          key: "accounts_receivable_protested",
          title: "Contas a receber protestadas (365 dias)",
          icon: "mdi-alert-octagon",
          iconColor: "#880E4F",
          cardClass: "summary-card--protested",
        },
        {
          key: "checks_in_custody_total",
          title: "Cheques em custódia",
          icon: "mdi-note",
          iconColor: "#1B5E20",
          cardClass: "summary-card--checks",
        },
      ],
      totalMetricDefs: [
        { key: "accounts_receivable_open", label: "Contas a receber em aberto" },
        { key: "accounts_receivable_overdue", label: "Contas a receber vencidas (365 dias)" },
        { key: "accounts_receivable_protested", label: "Contas a receber protestadas (365 dias)" },
        { key: "checks_in_custody_total", label: "Cheques em custódia" },
        { key: "stock_balance_total", label: "Saldo em estoque (soma galpões)" },
      ],
    }
  },
  computed: {
    referenceDateLabel() {
      return this.$moment(this.referenceDate).format("DD/MM/YYYY")
    },
  },
  methods: {
    toCurrency(value) {
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value || 0))
    },
    totalLinesFor(totals, kind) {
      const totalsMap = totals || EMPTY_TOTALS
      return this.totalMetricDefs.map((totalMetricDef) => {
        const isStock = totalMetricDef.key === "stock_balance_total"
        let display = this.toCurrency(totalsMap[totalMetricDef.key])
        if (isStock && kind === "wholesale") {
          display = "—"
        }
        let label = totalMetricDef.label
        if (isStock && kind === "grand") {
          label = "Saldo em estoque (todos os galpões)"
        }
        return { key: totalMetricDef.key, label, display }
      })
    },
    getData() {
      this.loading = true
      const params = {
        reference_date: this.referenceDate,
        consolidated: true,
      }
      Api.FinancialConsolidatedReport.index(params)
        .then(({ data }) => {
          const payload = data.data || {}
          this.consolidated = {
            per_modality: payload.per_modality || [],
            totals_retail: { ...EMPTY_TOTALS, ...(payload.totals_retail || {}) },
            totals_wholesale: { ...EMPTY_TOTALS, ...(payload.totals_wholesale || {}) },
            grand_totals: { ...EMPTY_TOTALS, ...(payload.grand_totals || {}) },
          }
        })
        .catch(() => {
          this.consolidated = {
            per_modality: [],
            totals_retail: { ...EMPTY_TOTALS },
            totals_wholesale: { ...EMPTY_TOTALS },
            grand_totals: { ...EMPTY_TOTALS },
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
  mounted() {
    this.getData()
  },
}
</script>

<style scoped>
.summary-card {
  min-height: 98px;
  border-radius: 10px;
  border: 1px solid #eceff1;
}

.summary-card-title {
  font-size: 12px;
  line-height: 1.2;
  color: #546e7a;
}

.summary-card-value {
  font-size: 20px;
  font-weight: 700;
  color: #263238;
}

.summary-card-value--muted {
  color: #90a4ae;
  font-weight: 600;
}

.summary-card--open {
  background: linear-gradient(180deg, #e3f2fd 0%, #ffffff 100%);
}

.summary-card--overdue {
  background: linear-gradient(180deg, #fff3e0 0%, #ffffff 100%);
}

.summary-card--protested {
  background: linear-gradient(180deg, #fce4ec 0%, #ffffff 100%);
}

.summary-card--checks {
  background: linear-gradient(180deg, #e8f5e9 0%, #ffffff 100%);
}

.summary-card--stock {
  background: linear-gradient(180deg, #eceff1 0%, #ffffff 100%);
}

.overview-card {
  border-radius: 10px;
  border: 1px solid #eceff1;
  background: linear-gradient(180deg, #fafafa 0%, #ffffff 100%);
}

.modality-section-card {
  border-radius: 10px;
  border: 1px solid #eceff1;
}

.totals-wrap-card {
  border-radius: 10px;
  border: 1px solid #eceff1;
}

.total-aggregate-card {
  border-radius: 10px;
  border: 1px solid #eceff1;
  height: 100%;
}

.total-aggregate-card--grand {
  background: linear-gradient(180deg, #e0f2f1 0%, #ffffff 100%);
}

.total-line {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.total-line:last-child {
  border-bottom: none;
}

.total-line__label {
  font-size: 12px;
  color: #546e7a;
  line-height: 1.3;
  flex: 1;
}

.total-line__value {
  font-size: 14px;
  font-weight: 700;
  color: #263238;
  white-space: nowrap;
}

.stock-value-header__icon {
  cursor: help;
  opacity: 0.75;
  transition: opacity 0.15s ease;
}

.stock-value-header__icon:hover {
  opacity: 1;
}

.overview-card-skeleton ::v-deep .transparent-skeleton .v-skeleton-loader__bone {
  background: rgba(0, 0, 0, 0.06) !important;
}
</style>
